import { Fragment, useMemo } from 'react';

import { DigitalQuote } from '@frontend/api';
import {
  Box,
  OutlineCircleInfoIcon,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  TableContainer,
  TableV2,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@frontend/components';
import { useDateFormat } from '@frontend/utils';

import { useAppSelector } from 'store/hooks';
import { getOrder } from 'store/orders/selectors';

import { formatLeadTimeLabel, formatPrice } from 'utils/formatters';

import { DRAWER_MODES } from '../constants';
import {
  getDrawerMode,
  getQuoteItems,
  getSelectedOptions,
  getSubtotalCost,
  getTaxCost,
  getTotalCost,
} from '../data/reducer';
import { getShipsOnDateFromLeadTime } from '../utils';

type ReviewOrderProps = {
  quote: DigitalQuote;
};

export const ReviewOrder = ({ quote }: ReviewOrderProps) => {
  const selectedOptions = useAppSelector(getSelectedOptions);
  const subtotal = useAppSelector(getSubtotalCost);
  const quoteItems = useAppSelector(getQuoteItems);
  const quoteItemsById = useMemo(
    () =>
      Object.fromEntries(
        quoteItems.map((quoteItem) => [quoteItem.id, quoteItem]),
      ),
    [quoteItems],
  );
  const maxUnitPricePrecision = useMemo(() => {
    return Math.max(
      ...quoteItems.map(
        (quoteItem) => quoteItem.rootComponent.unitPricePrecision,
      ),
    );
  }, [quoteItems]);

  const drawerMode = useAppSelector(getDrawerMode);
  const order = useAppSelector(getOrder);
  const orderCreatedDate =
    drawerMode === DRAWER_MODES.EDIT && order ? order.created : undefined;

  const { toDateString: toPreferredDateFormat } = useDateFormat();

  const taxCost = useAppSelector((state) => getTaxCost(state, quote));
  const totalCost = useAppSelector((state) => getTotalCost(state, quote));

  return (
    <TableContainer
      border="none"
      borderRadius={0}
      size="sm"
      variant="simple"
      height="100%"
      overflow="auto"
    >
      <TableV2>
        <Thead position="sticky" top={0} zIndex={2}>
          <Tr>
            <Th />
            <Th maxW={23}>Part</Th>
            <Th textAlign="end">Qty</Th>
            <Th textAlign="end">Lead Time</Th>
            <Th>Ships On</Th>
            <Th textAlign="end">
              Unit Price
              <Tooltip label="Includes any discounts">
                <OutlineCircleInfoIcon
                  boxSize={2}
                  color="feature.secondary"
                  ml={0.25}
                />
              </Tooltip>
            </Th>
            <Th textAlign="end">
              Total Price
              <Tooltip label="Includes any discounts">
                <OutlineCircleInfoIcon
                  boxSize={2}
                  color="feature.secondary"
                  ml={0.25}
                />
              </Tooltip>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {Object.entries(selectedOptions).map(
            ([quoteItemId, selectedOption], index) => {
              const item = selectedOption.expedite ?? selectedOption.quantity;
              const leadTime =
                selectedOption.manualLeadTime ??
                item.manualLeadTime ??
                item.leadTime;
              const shipsOn =
                selectedOption.shipsOn ??
                getShipsOnDateFromLeadTime({
                  fromDate: orderCreatedDate
                    ? new Date(orderCreatedDate)
                    : new Date(),
                  leadTime,
                  leadTimeDisplayUnit:
                    selectedOption.quantity.leadTimeDisplayUnit,
                }).toISOString();
              const quoteItem = quoteItemsById[quoteItemId];
              const rootComponent = quoteItem.rootComponent;
              const addOns = Object.entries(selectedOption.addOns).reduce<
                { addOnCellId: number; name: string; price: string }[]
              >((currAddOns, [addOnId, addOnCell]) => {
                const addOn = rootComponent.addOns.find(
                  (addOn) => addOn.id.toString() === addOnId,
                );
                if (addOn) {
                  currAddOns.push({
                    addOnCellId: addOnCell.id,
                    name: addOn.name,
                    price: addOnCell.manualPrice ?? addOnCell.price,
                  });
                }
                return currAddOns;
              }, []);

              return (
                <Fragment key={quoteItemId}>
                  <Tr>
                    <Td textAlign="end" fontFamily="data">
                      {index + 1}
                    </Td>
                    <Td maxW={23} innerProps={{ padding: 0 }}>
                      <Part
                        size="sm"
                        variant="borderless"
                        number={rootComponent.partNumber}
                        rev={rootComponent.revision ?? undefined}
                        filename={rootComponent.part.filename}
                        border="none"
                      >
                        <PartThumbnailContainer borderRadius={0}>
                          <PartThumbnail
                            partFileUuid={rootComponent.part.primaryFileId}
                            filename={rootComponent.part.filename}
                            isBoxShadowVisible
                            containsVirus={rootComponent.part.containsVirus}
                          />
                        </PartThumbnailContainer>
                        <PartLabel />
                      </Part>
                    </Td>
                    <Td textAlign="end">{selectedOption.quantity.quantity}</Td>
                    <Td textAlign="end">
                      {formatLeadTimeLabel(leadTime, {
                        unit: item.leadTimeDisplayUnit,
                      }).toLowerCase()}
                    </Td>
                    <Td>{toPreferredDateFormat(shipsOn)}</Td>
                    <Td textAlign="end" fontFamily="data" fontWeight="heavy">
                      {formatPrice(
                        selectedOption.expedite?.unitPrice ??
                          selectedOption.quantity.totalUnitPrice,
                        rootComponent.unitPricePrecision,
                      )}
                    </Td>
                    <Td textAlign="end" fontFamily="data" fontWeight="heavy">
                      {formatPrice(
                        item.totalPrice,
                        rootComponent.unitPricePrecision,
                      )}
                    </Td>
                  </Tr>
                  {!!addOns.length && (
                    <Tr>
                      <Th as="td" colSpan={6} fontWeight="default" pl={2}>
                        {addOns.map((addOn) => (
                          <Box key={addOn.addOnCellId}>{addOn.name}</Box>
                        ))}
                      </Th>
                      <Th
                        as="td"
                        textAlign="end"
                        fontWeight="default"
                        fontFamily="data"
                      >
                        {addOns.map((addOn) => (
                          <Box key={addOn.addOnCellId}>
                            {formatPrice(
                              addOn.price,
                              rootComponent.unitPricePrecision,
                            )}
                          </Box>
                        ))}
                      </Th>
                    </Tr>
                  )}
                </Fragment>
              );
            },
          )}
        </Tbody>
        <Tfoot>
          <Tr>
            <Th colSpan={6} fontWeight="medium">
              Subtotal
            </Th>
            <Th as="td" textAlign="end" fontFamily="data" fontWeight="default">
              {formatPrice(subtotal, maxUnitPricePrecision)}
            </Th>
          </Tr>
          <Tr>
            <Th colSpan={6} fontWeight="medium">
              Tax
            </Th>
            <Th as="td" textAlign="end" fontFamily="data" fontWeight="default">
              {formatPrice(taxCost, maxUnitPricePrecision)}
            </Th>
          </Tr>
          <Tr>
            <Th colSpan={6}>Order Total</Th>
            <Th as="td" textAlign="end" fontFamily="data">
              {formatPrice(totalCost, maxUnitPricePrecision)}
            </Th>
          </Tr>
        </Tfoot>
      </TableV2>
    </TableContainer>
  );
};
